import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Globals } from '@aiwyn/config';
import { EsignSigningContextResponse, SubmitEsignSignatureRequest, SubmitEsignSignatureResponse } from '../models';

@Injectable({ providedIn: 'root' })
export class EsignSigningService {
  private readonly http = inject(HttpClient);
  private readonly globals = inject(Globals);
  private get base(): string { return `${this.globals.artUrl}/api/secure/client/esign/v1`; }

  getSigningContext(bundleId?: number | null, recipientId?: number | null): Observable<EsignSigningContextResponse> {
    return this.http.get<EsignSigningContextResponse>(`${this.base}/signing-context`, this.options(bundleId, recipientId));
  }

  acceptConsent(bundleId?: number | null, recipientId?: number | null): Observable<void> {
    return this.http.post<void>(`${this.base}/consent`, null, this.options(bundleId, recipientId));
  }

  submitSignature(
    payload: SubmitEsignSignatureRequest,
    bundleId?: number | null,
    recipientId?: number | null,
  ): Observable<SubmitEsignSignatureResponse> {
    return this.http.post<SubmitEsignSignatureResponse>(`${this.base}/sign`, payload, this.options(bundleId, recipientId));
  }

  /**
   * Both ids are sent only by the authenticated portal caller. A secure link names its
   * one recipient row on its own, and passing ids alongside it would suggest the caller
   * gets to choose what they are signing — they do not; the backend resolves the link's
   * own row and ignores anything else.
   *
   * `recipientId` says which of the caller's own rows on the bundle they are acting as,
   * for a bundle that addresses the same person twice. It narrows within what the
   * caller already holds; the backend refuses a row that is not theirs.
   */
  private options(bundleId?: number | null, recipientId?: number | null): { params?: HttpParams } {
    if (bundleId == null) return {};
    const params = new HttpParams().set('bundleId', bundleId);
    return { params: recipientId == null ? params : params.set('recipientId', recipientId) };
  }
}
